import { useNavigate, useRouter } from '@tanstack/react-router';
import { useCallback } from 'react';
import { CbccLokiKey, logCbccLokiInfo } from '@/lib/loki-logger';
import { CBCC_USER_FLOW } from '../types';
import { useCbccOnboard } from './use-cbcc-onboard';
import { useManageCard } from './use-manage-card';

// The single dispatcher for the apply CTA's `userFlow`, shared by the CTA click
// (CbccCta) and the post-login auto-resume (CbccPostLoginResume) so both behave
// identically:
//   REDIRECT_TO_FORM        → /travel-credit-card/apply (new applicant)
//   ONBOARDING(_PENDING)    → POST /cbcc/onboard, then form-POST to the bank
//   APPROVED(_NO_POPUP)     → GET /hf/manage/card redirect to the bank
//   else (REJECTED/unknown) → no-op
// The onboard/manage flows end in a full-page bank redirect; `isLoading` stays
// true through that navigation (released on bfcache restore — see the hooks).
//
// Returns `true` when a recognized flow was initiated (a navigation/redirect is
// now in flight) and `false` when nothing was dispatched (REJECTED / unknown /
// null). Callers use this to keep a click spinner ON when redirecting and
// release it on a dead end — see CbccCta's logged-in CVP-recovery path.
export function useCbccFlow() {
	const navigate = useNavigate();
	const router = useRouter();
	const { startOnboarding, isLoading: isOnboarding } = useCbccOnboard();
	const { openManageCard, isLoading: isOpeningManageCard } = useManageCard();

	const isLoading = isOnboarding || isOpeningManageCard;

	const dispatchFlow = useCallback(
		(userFlow: string | null | undefined): boolean => {
			// Re-entrancy guard: a bank handoff is already in flight — report it as
			// dispatched so a caller's spinner stays on.
			if (isLoading) return true;
			switch (userFlow) {
				case CBCC_USER_FLOW.REDIRECT_TO_FORM:
					navigate({ to: '/travel-credit-card/apply' });
					return true;
				case CBCC_USER_FLOW.ONBOARDING:
				case CBCC_USER_FLOW.ONBOARDING_PENDING:
					startOnboarding();
					return true;
				case CBCC_USER_FLOW.APPROVED:
				case CBCC_USER_FLOW.APPROVED_NO_POPUP:
					openManageCard();
					return true;
				default:
					// No recognized flow. Two cases, both previously a SILENT dead
					// button (the user clicked and nothing happened):
					//  - userFlow == null → CVP never resolved for this user (fetch
					//    failed / stale-`at` 401 in the loader). The CTA recovers by
					//    re-running the loader and auto-dispatching the fresh flow (see
					//    CbccCta); the invalidate here covers callers that don't (the
					//    post-login resume path) so a transient failure still self-heals.
					//  - REJECTED / unknown string → genuinely nothing to dispatch.
					// Log either way so it's diagnosable in prod instead of silent.
					logCbccLokiInfo(CbccLokiKey.CTA_NOOP, {
						userFlow: userFlow ?? 'null',
					});
					if (userFlow == null) router.invalidate();
					return false;
			}
		},
		[isLoading, navigate, router, startOnboarding, openManageCard],
	);

	return { dispatchFlow, isLoading };
}
